import { ReactNode } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { Wordmark } from "@/components/brand";
import { InteractiveBackdrop } from "@/components/InteractiveBackdrop";
import { SiteFooter } from "@/components/SiteFooter";

/**
 * PublicPageLayout - the lightweight marketing chrome shared by every public
 * resource page (/guides, /playbooks, /status).
 *
 * These pages must render for BOTH logged-out and logged-in visitors, so the
 * top-bar action adapts to the session (passed down from App, which already
 * owns the single source of auth truth - no duplicate auth subscription, no
 * first-frame flash of the wrong CTA):
 *   - logged out  -> "Sign in" (ghost) + "Get started" (primary -> /signup)
 *   - logged in   -> "Open app" (primary -> /app)
 * The wordmark always links to the appropriate home (landing or app).
 *
 * Layout = sticky top bar + InteractiveBackdrop + page content + the shared
 * SiteFooter (landing variant, so its Product links smooth-scroll the marketing
 * sections and its Resources point at the other public pages). Reduced-motion is
 * handled by InteractiveBackdrop + the global media rule in index.css.
 */
export function PublicPageLayout({ children, authed = false }: { children: ReactNode; authed?: boolean }) {
  const navigate = useNavigate();
  // Logged-in users go to the app home; logged-out users to the landing page.
  const homeTo = authed ? "/app" : "/";

  return (
    <div className="relative min-h-screen flex flex-col overflow-x-hidden">
      <InteractiveBackdrop />

      <header className="sticky top-0 z-30 border-b border-[#1a2640]/80 bg-[#020617]/80 backdrop-blur-xl">
        <div className="absolute inset-x-0 bottom-0 h-px bg-gradient-to-r from-transparent via-brand-600/25 to-transparent" />
        <div className="max-w-6xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between gap-4">
          <Link to={homeTo} className="cursor-pointer shrink-0" aria-label="NovaSynth home">
            <Wordmark size="md" showTagline />
          </Link>
          <div className="flex items-center gap-2">
            {authed ? (
              <button
                onClick={() => navigate("/app")}
                className="group inline-flex items-center gap-1.5 rounded-lg bg-gradient-to-r from-brand-600 to-brand-500 px-3.5 py-2 text-xs font-semibold text-white shadow-glow-brand-sm transition-all hover:from-brand-500 hover:to-brand-400 cursor-pointer min-h-[40px]"
              >
                Open app
                <ArrowRight className="w-3.5 h-3.5 transition-transform duration-200 group-hover:translate-x-0.5" />
              </button>
            ) : (
              <>
                <button
                  onClick={() => navigate("/signin")}
                  className="px-3.5 py-2 text-xs font-medium text-slate-300 hover:text-white hover:bg-white/5 rounded-lg transition-colors cursor-pointer min-h-[40px]"
                >
                  Sign in
                </button>
                <button
                  onClick={() => navigate("/signup")}
                  className="inline-flex items-center gap-1.5 rounded-lg bg-gradient-to-r from-brand-600 to-brand-500 px-3.5 py-2 text-xs font-semibold text-white shadow-glow-brand-sm transition-all hover:from-brand-500 hover:to-brand-400 cursor-pointer min-h-[40px]"
                >
                  Get started
                </button>
              </>
            )}
          </div>
        </div>
      </header>

      <main className="relative z-10 flex-1 w-full">{children}</main>

      <SiteFooter variant="landing" />
    </div>
  );
}

// ── Shared building blocks for the resource pages ─────────────────────────────

/**
 * PageHero - the top band of a resource page: an eyebrow, big headline, and a
 * supporting line, centered in the reading column.
 */
export function PageHero({
  eyebrow,
  title,
  sub,
}: {
  eyebrow: string;
  title: ReactNode;
  sub?: ReactNode;
}) {
  return (
    <div className="ns-reveal mx-auto max-w-3xl text-center">
      <span className="inline-flex items-center gap-2 rounded-full border border-[#1a2640] bg-white/[0.03] px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.2em] text-[#22d3ee]">
        {eyebrow}
      </span>
      <h1 className="mt-5 font-mono text-3xl sm:text-4xl font-bold tracking-tight text-white">
        {title}
      </h1>
      {sub && (
        <p className="mx-auto mt-4 max-w-2xl text-base leading-relaxed text-slate-400">{sub}</p>
      )}
    </div>
  );
}

/** Section heading used inside resource pages (left aligned, with eyebrow). */
export function SectionHead({
  eyebrow,
  title,
  sub,
}: {
  eyebrow?: string;
  title: string;
  sub?: string;
}) {
  return (
    <div className="ns-reveal">
      {eyebrow && (
        <span className="text-[10px] font-semibold uppercase tracking-[0.2em] text-[#22d3ee]">
          {eyebrow}
        </span>
      )}
      <h2 className="mt-2 font-mono text-2xl font-bold tracking-tight text-white">{title}</h2>
      {sub && <p className="mt-3 max-w-2xl text-sm leading-relaxed text-slate-400">{sub}</p>}
    </div>
  );
}
